import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { NgbModal, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Practitioner } from '../../models/practitioner.model';
import { FormGroup, FormControl, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Booking } from '../../models/booking.model';
import { BookingService } from '../../services/booking.service';
import { Specialty } from '../../models/specialty.model';
import { Therapy } from '../../models/therapy.model';
import { BookingType } from '../../enums/booking-type.enum';
import { BookingTime } from '../../enums/booking-time.enum';
import { BookingDate } from '../../models/booking-date.model';

@Component({
  selector: 'app-booking-form',
  templateUrl: './booking-form.component.html',
  styleUrls: ['./booking-form.component.scss']
})
export class BookingFormComponent implements OnInit {
  @ViewChild('content') content: any;
  @Input() practitioner: Practitioner;
  @Input() servicesSelected: Therapy[] = new Array<Therapy>();
  @Output() bookingCompleted: EventEmitter<string> = new EventEmitter();

  bookingForm: FormGroup;
  disableSubmit: boolean = false;
  isLoading: boolean = false;
  error: string = null;
  allTherapies: Therapy[] = new Array<Therapy>();
  bookingTypes = Object.values(BookingType);
  bookingTimes = Object.values(BookingTime);

  constructor(private modalService: NgbModal, private bookingService: BookingService) { }

  ngOnInit(): void {
    this._loadTherapies();
  }

  _loadTherapies(): void {
    this.practitioner.specialties.forEach((specialty: Specialty) => {
      this.allTherapies = this.allTherapies.concat(specialty.therapies);
    });
  }

  open() {
    this.modalService.open(this.content, { ariaLabelledBy: 'modal-basic-title', size: 'lg', scrollable: true }).result.then(_ => {
      this.bookingForm.reset();
    }, _ => {
      this.bookingForm.reset();
    });

    this._initializeForm();
  }

  _initializeForm(): void {
    this.bookingForm = new FormGroup({
      'services': new FormArray([
        new FormControl(null, Validators.required)
      ]),
      'description': new FormControl(null, Validators.required),
      'requestedDates': new FormGroup({
        'dates': new FormArray([
          new FormControl(null, Validators.required),
          new FormControl(null, Validators.required),
          new FormControl(null, Validators.required)
        ]),
        'times': new FormArray([
          new FormControl(BookingTime.Morning, Validators.required),
          new FormControl(BookingTime.Morning, Validators.required),
          new FormControl(BookingTime.Morning, Validators.required)
        ])
      }),
      'firstName': new FormControl(null, Validators.required),
      'lastName': new FormControl(null, Validators.required),
      'email': new FormControl(null, [Validators.required, Validators.email]),
      'phoneNumber': new FormControl(null, Validators.required),
      'referredBy': new FormControl(null, Validators.required),
      'preferredContact': new FormControl('phone', Validators.required),
      'bookingType': new FormControl(BookingType.InPerson, Validators.required)
    });

    if (this.servicesSelected.length > 0) {
      var serviceArray: FormArray = new FormArray([]);
      this.servicesSelected.forEach((therapy: Therapy) => {
        var serviceControl = new FormControl(therapy.name, Validators.required);
        serviceArray.push(serviceControl);
      });
      this.bookingForm.setControl('services', serviceArray);
    }
  }

  _onAddControl(type: string) {
    (<FormArray>this.bookingForm.get(type)).push(new FormControl(null));
  }

  _onRemoveControl(type: string, index: number) {
    (<FormArray>this.bookingForm.get(type)).removeAt(index);
  }

  _getControls(type: string): AbstractControl[] {
    return (<FormArray>this.bookingForm.get(type)).controls;
  }

  _setLookupValue(value: string, path: string) {
    this.bookingForm.get(path).setValue(value);
    this.bookingForm.get(path).markAsTouched();
    this.bookingForm.get(path).updateValueAndValidity();
  }

  onSubmit() {
    if (this.bookingForm.invalid) { return; }
    this.disableSubmit = true;
    this.isLoading = true;
    this.error = null;
    var date: NgbDate = this.bookingForm.value.requestedDate;


    var booking: Booking = new Booking(
      this.bookingForm.value.description,
      this.bookingForm.value.email,
      this.bookingForm.value.firstName,
      false,
      this.bookingForm.value.lastName,
      this.bookingForm.value.phoneNumber,
      this.practitioner.id,
      this.bookingForm.value.preferredContact,
      this.bookingForm.value.referredBy,
      this._mapDatesRequested(),
      this._mapTherapies(),
      this.bookingForm.value.bookingType,
      new Date().getTime(),
      new Date().getTime()
    );

    this.bookingService.createBooking(this.practitioner, booking).then(_ => {
      this.isLoading = false;
      this.modalService.dismissAll();
      this.bookingForm.reset();
      this.bookingCompleted.emit('success');
    }, error => {
      this.error = error;
      this.disableSubmit = false;
      this.isLoading = false;
    });
  }

  _mapDatesRequested(): BookingDate[] {
    let bookingDates: BookingDate[] = new Array<BookingDate>();
    let values = this.bookingForm.value.requestedDates;
    values.dates.forEach((requestedDate, index) => {
      var date: NgbDate = requestedDate;
      var actualDate: Date = new Date(date.year, date.month - 1, date.day);
      bookingDates.push(new BookingDate(actualDate.getTime(), values.times[index]));
    });
    return bookingDates;
  }

  _mapTherapies(): Therapy[] {
    var therapies: Therapy[] = new Array<Therapy>();
    let value = this.bookingForm.value.services;

    value.forEach((therapy: string) => {
      let tempTherapy = this.allTherapies.find(t => t.name === therapy);
      therapies.push(tempTherapy);
    });

    return therapies;
  }
}
